import { motion } from "framer-motion";
import { Check, X, ArrowRightLeft, AlertTriangle } from "lucide-react";

const STATUS_CONFIG = {
  KEEP: {
    icon: <Check className="w-3 h-3" />,
    badge: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    dot: "bg-emerald-400",
    dotGlow: "shadow-emerald-400/30",
  },
  // Mapping both DROP and KILL to Red
  DROP: {
    icon: <X className="w-3 h-3" />,
    badge: "bg-red-500/10 text-red-400 border-red-500/20",
    dot: "bg-red-400",
    dotGlow: "shadow-red-400/30",
  },
  KILL: {
    icon: <X className="w-3 h-3" />,
    badge: "bg-red-500/10 text-red-400 border-red-500/20",
    dot: "bg-red-400",
    dotGlow: "shadow-red-400/30",
  },
  // Mapping both SWAP and WEAK to Amber
  SWAP: {
    icon: <ArrowRightLeft className="w-3 h-3" />,
    badge: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    dot: "bg-amber-400",
    dotGlow: "shadow-amber-400/30",
  },
  WEAK: {
    icon: <AlertTriangle className="w-3 h-3" />,
    badge: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    dot: "bg-amber-400",
    dotGlow: "shadow-amber-400/30",
  },
};

export default function KeepDropList({ cards }) {
  if (!cards || cards.length === 0) return null;

  return (
    <div
      className="glass-card rounded-2xl p-6 transition-all duration-300 h-full border border-white/5"
      data-testid="keep-drop-list"
    >
      <p className="text-xs font-black text-white/20 uppercase tracking-[0.2em] mb-6">
        Portfolio Audit
      </p>

      <div className="space-y-3">
        {cards.map((card, i) => {
          // Normalize status to uppercase and fallback to KEEP if unknown
          const statusKey = card.status?.toUpperCase() || 'KEEP';
          const config = STATUS_CONFIG[statusKey] || STATUS_CONFIG.KEEP;

          return (
            <motion.div
              key={card.name + i}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: i * 0.06 }}
              className="group flex items-start gap-4 p-4 rounded-2xl bg-white/[0.02] hover:bg-white/[0.05] transition-all border border-white/5"
              data-testid={`card-item-${i}`}
            >
              {/* Status dot with Glow */}
              <div
                className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${config.dot} ${config.dotGlow} shadow-[0_0_10px_rgba(0,0,0,0.5)]`}
              />

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span
                    className="font-bold text-sm text-white/90 truncate tracking-tight"
                    data-testid={`card-name-${i}`}
                  >
                    {card.name}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-[9px] font-black rounded-full border ${config.badge} transition-colors`}
                    data-testid={`card-status-${i}`}
                  >
                    {config.icon}
                    {statusKey}
                  </span>
                </div>
                
                <p
                  className="text-xs text-white/40 leading-relaxed mb-3"
                  data-testid={`card-reason-${i}`}
                >
                  {card.reason}
                </p>

                <div className="flex items-center gap-4 border-t border-white/[0.03] pt-3">
                  <div className="flex flex-col">
                    <span className="text-[8px] uppercase tracking-widest text-white/20 font-bold">Annual Fee</span>
                    <span className="text-xs font-mono text-white/60">
                      ₹{card.annual_fee?.toLocaleString("en-IN")}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[8px] uppercase tracking-widest text-white/20 font-bold">Net Gain</span>
                    <span
                      className={`text-xs font-mono font-bold ${card.yearly_gain >= 0 ? "text-emerald-400/80" : "text-red-400/80"}`}
                    >
                      ₹{card.yearly_gain?.toLocaleString("en-IN")}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
